import React, { useState, useEffect } from "react";
import { CreditCard, CheckCircle, Clock, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { useAuth } from "../../contexts";
import { apiCall } from "../../lib/api";
import { useNavigate } from "react-router-dom";

export const FeeTrackerWidget = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user && token) {
      apiCall("GET", "/fees-tracker", null, token)
        .then(setData)
        .catch(() => {
          setData({
            total_fees: 2500,
            paid: 1750,
            term: "Term 2, 2025-2026",
            payments: [
              { date: "2025-09-15", amount: 1000, method: "Bank Transfer", reference: "PAY-001" },
              { date: "2025-11-20", amount: 500, method: "Mobile Money", reference: "PAY-002" },
              { date: "2026-01-10", amount: 250, method: "Cash", reference: "PAY-003" },
            ],
          });
        });
    }
  }, [user, token]);

  if (!data) return null;

  const remaining = data.total_fees - data.paid;
  const percentage = Math.round((data.paid / data.total_fees) * 100);
  const isPaid = remaining <= 0;

  return (
    <Card className="bg-white border-2 border-green-100 overflow-hidden" data-testid="fee-tracker-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CreditCard size={20} className="text-green-600" />
            <h3 className="font-bold text-[#002147] text-sm">Fee Tracker</h3>
          </div>
          <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{data.term}</span>
        </div>

        {/* Progress circle */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative w-16 h-16">
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none" stroke="#e5e7eb" strokeWidth="3"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke={isPaid ? "#10B981" : "#FF6B00"}
                strokeWidth="3"
                strokeDasharray={`${percentage}, 100`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-black text-[#002147]">{percentage}%</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Paid</span>
              <span className="font-bold text-green-600">GHS {data.paid.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">Remaining</span>
              <span className={`font-bold ${isPaid ? "text-green-600" : "text-red-500"}`}>GHS {remaining.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Total</span>
              <span className="font-bold text-[#002147]">GHS {data.total_fees.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Recent payments */}
        <div className="space-y-2 mb-3">
          {(data.payments || []).slice(0, 2).map((p, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-500" />
                <div>
                  <p className="text-xs font-medium text-[#002147]">GHS {p.amount}</p>
                  <p className="text-[10px] text-gray-400">{p.method}</p>
                </div>
              </div>
              <span className="text-[10px] text-gray-400">{p.date}</span>
            </div>
          ))}
        </div>

        {!isPaid && (
          <button
            onClick={() => navigate("/fees")}
            className="w-full py-2.5 bg-[#FF6B00] text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1 shadow-[0_4px_0_rgba(0,0,0,0.15)] hover:shadow-[0_2px_0_rgba(0,0,0,0.15)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-150"
            data-testid="pay-fees-btn"
          >
            Pay Now <ChevronRight size={14} />
          </button>
        )}
        {isPaid && (
          <div className="text-center py-2">
            <span className="text-green-600 text-xs font-bold flex items-center justify-center gap-1">
              <CheckCircle size={14} /> Fees Fully Paid!
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeeTrackerWidget;
